import React, { useEffect, useState } from "react";
import PaymentDialog from "./PaymentDialog";
import { CloseOutlined } from "@mui/icons-material";
import { Dialog } from "@mui/material";
import { toast } from "react-toastify";
import { PaymentDetails } from "../../models/PaymentDetails";
import API from "../../services";
import { RootState } from "../../models/RootState";
import { connect } from "react-redux";
import { UserDetails } from "../../models/UserDetails";
import "./DepositForm.scss";
import DownArrow from '../../assets/images/down-arrow.svg';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QRCode from "react-qr-code";
import { SiteConfig } from "../../models/SiteConfig";
import { demoUser } from "../../utils/StringUtils";
import { useTranslation } from "../../hooks/useTranslation";
import { getTextColorFromCSSVar } from "../../store/authActions";

interface PGItem {
  accountId: string;
  tgProviderId: string;
  category: string;
  pgName?: string;
  minDeposit: number;
  maxDeposit: number;
  minWithdrawal: number;
  maxWithdrawal: number;
  applicationName: string;
}

interface DepositFormProps {
  activeTab: string;
  action: string;
  userDetails: UserDetails;
  pgList: PGItem[];
  siteConfig: SiteConfig;
  dynamicTextColors?: any;
}

const DepositForm: React.FC<DepositFormProps> = (props) => {
  const { activeTab, userDetails, pgList, siteConfig, dynamicTextColors } = props;
  const { t } = useTranslation();
  const isDemo = demoUser();
  const [amount, setAmount] = useState<number | undefined>(undefined);
  const [showPayment, setShowPayment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentProps, setPaymentProps] = useState<PaymentDetails | null>(null);

  // Get dynamic text color classes from Redux instead of calling function
  const pageTextColorClass = dynamicTextColors?.pageTextColorClass || '';
  const inputBoxTextColorClass = dynamicTextColors?.inputBoxTextColorClass || '';
  const buttonTextColorClass = dynamicTextColors?.buttonTextColorClass || '';

  // tabTextColorClass uses same as inputBoxTextColorClass
  const tabTextColorClass = inputBoxTextColorClass;
  
  // activeTabTextColorClass uses same as buttonTextColorClass
  const activeTabTextColorClass = buttonTextColorClass;

  // Helper function to check if user is created from back office
  // Format: {botId}-a... (e.g., 1844-anm777) vs regular: {botId}-{number}... (e.g., 1844-916281755762)
  const isBackOfficeUser = () => {
    const userKey = userDetails?.UserKey || sessionStorage.getItem("userKey");
    if (!userKey) return false;
    
    const parts = userKey.split("-");
    if (parts.length < 2) return false;
    
    // Check if the part after the dash starts with lowercase 'a'
    const afterDash = parts[1];
    return afterDash && /^a/.test(afterDash);
  };

  const isDisabled = loading || amount === undefined || amount <= 0 || (!isBackOfficeUser() && userDetails?.MobileNumber === "" && !siteConfig?.isContactOptional);
  const userKey = new URLSearchParams(window.location.search).get("id");
  const [activeCurType, setActiveCurType] = useState<string>('Fiat');
  const [selectedCurrency, setSelectedCurrency] = useState<any>();
  const [isOpen, setIsOpen] = useState(false);
  const [currList, setCurrList] = useState<any>([]);
  const [walletAddress, setWalletAddress] = useState<any>("");
  const [selectedPG, setSelectedPG] = useState<PGItem | null>(null);
  const [fiatPGList, setFiatPGList] = useState<PGItem[]>([]);
  const [cryptoPGList, setCryptoPGList] = useState<PGItem[]>([]);

  // Filter PG list based on active tab/category and set defaults
  useEffect(() => {
    if (isDemo) return;
    const fiatList = pgList?.filter((pg: PGItem) => pg.category.toLowerCase() === 'fiat') ?? [];
    const cryptoList = pgList?.filter((pg: PGItem) => pg.category.toLowerCase() === 'crypto') ?? [];

    setFiatPGList(fiatList);
    setCryptoPGList(cryptoList);

    // Ensure active tab is one that has items
    if (activeCurType === 'Fiat' && fiatList.length === 0 && cryptoList.length > 0) {
      setActiveCurType('Crypto');
      setSelectedPG(cryptoList[0] ?? null);
      return;
    }
    if (activeCurType === 'Crypto' && cryptoList.length === 0 && fiatList.length > 0) {
      setActiveCurType('Fiat');
      setSelectedPG(fiatList[0] ?? null);
      return;
    }

    // Set default selected PG based on current active tab
    if (activeCurType === 'Fiat') {
      setSelectedPG(fiatList.length > 0 ? fiatList[0] : null);
    } else if (activeCurType === 'Crypto') {
      setSelectedPG(cryptoList.length > 0 ? cryptoList[0] : null);
    }
  }, [pgList, activeCurType]);

  // Call getCurrList when selectedPG changes and we're in Crypto tab
  useEffect(() => {
    if (isDemo) return;
    if (activeCurType === "Crypto" && selectedPG && cryptoPGList?.length > 0) {
      // Only call getCurrList if selectedPG is part of cryptoPGList
      const isCryptoPG = cryptoPGList.some(pg => pg.tgProviderId === selectedPG.tgProviderId);
      if (isCryptoPG) {
        getCurrList();
      }
    }
  }, [selectedPG, activeCurType]);

  const closeDialog = () => {
    setShowPayment(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isDemo) return;

    if (amount === undefined || Number.isNaN(amount) || amount <= 0) {
      toast.error(t("wallet.messages.pleaseEnterValidAmount"));
      return;
    }

    setLoading(true);
    try {
      const response = await API.post(`/account/initiateDeposit`, {
        uKey: sessionStorage.getItem("userKey"),
        amount: amount,
        currency: "INR",
        tgProviderId: selectedPG?.tgProviderId,
        accountId: selectedPG?.accountId,
      });

      const data = response.data;
      if (data.status !== "RS_OK") {
        toast.error(`${data.errorDescription}`);
        return;
      }

      const paymentOptions: PaymentDetails = {
        referenceId: data.data.reference_id,
        bankId: "",
        qrCode: "",
        upiIntent: "",
        isUpi: false,
        actId: "",
        actName: "",
        ifsc: "",
        bankName: "",
        isBankTransfer: false,
        upiId: "",
      };

      data.data.payment_options.forEach((option: any) => {
        if (option.payment_method === "UPI") {
          const upiQr = option?.payment_method_details?.upi_qr;
          const upiIds = option?.payment_method_details?.upi_id;
          paymentOptions.bankId = option.payment_method_details.bank_id;
          paymentOptions.qrCode = Array.isArray(upiQr)
            ? upiQr.filter((qr: any) => typeof qr === "string" && qr.trim() !== "")
            : (typeof upiQr === "string" ? upiQr : "");
          paymentOptions.upiIntent =
            option.payment_method_details.upi_intent.replace("upi://", "");
          paymentOptions.isUpi = true;
          paymentOptions.upiIds = Array.isArray(upiIds)
            ? upiIds.filter((id: any) => typeof id === "string" && id.trim() !== "")
            : (typeof upiIds === "string" && upiIds.trim() !== "" ? [upiIds] : []);
          paymentOptions.upiId = Array.isArray(upiIds) ? (upiIds[0] || "") : (upiIds || "");
        } else if (option.payment_method === "BANK_TRANSFER") {
          paymentOptions.actId =
            option.payment_method_details.bank_account_number;
          paymentOptions.actName =
            option.payment_method_details.bank_account_holder;
          paymentOptions.ifsc = option.payment_method_details.ifs_code;
          paymentOptions.bankName = option.payment_method_details.bank_name;
          paymentOptions.bankId = option.payment_method_details.bank_id;
          paymentOptions.isBankTransfer = true;
        }
      });

      setPaymentProps(paymentOptions);
      setShowPayment(true);
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrList = async () => {
    if (isDemo) return;
    let response;
    try {
      response = await API.post(`account/get-crypto-currencies`, {
        tgProviderId: selectedPG?.tgProviderId,
        accountId: selectedPG?.accountId,
      });
    } catch (e) {
      return;
    }
    if (response && response.data?.currenciesList) {
      setCurrList(response?.data?.currenciesList)
    }
  }

  const getWalletAddress = async (curr: any) => {
    if (isDemo) return;
    let response;
    try {
      response = await API.post(`account/get-wallet-address`, {
        tgProviderId: selectedPG?.tgProviderId,
        currency: curr?.cryptoCurrency,
        networkId: curr?.networkId,
        accountId: selectedPG?.accountId,
      });
    } catch (e) {
      return;
    }
    if (response?.data?.status !== "RS_OK") {
      toast?.error(response?.data?.errorDescription)
    }

    if (response) {
      setWalletAddress(response?.data?.walletAddress)
    }
  }

  const handleTabClick = (tab: any) => {
    if (isDemo) return;
    // Prevent switching to a tab with no items
    if (tab === 'Fiat' && fiatPGList.length === 0) return;
    if (tab === 'Crypto' && cryptoPGList.length === 0) return;

    setActiveCurType(tab);
    if (tab === 'Fiat') {
      setSelectedPG(fiatPGList[0] ?? null);
    } else {
      setSelectedPG(cryptoPGList[0] ?? null);
      setLoading(false);
    }
  };

  const copyToClipboard = (text: any) => {
    navigator?.clipboard?.writeText(text)?.then(() => {
    });
  }

  const handleCurrencySelect = (curr: any) => {
    if (isDemo) return;
    setSelectedCurrency(curr);
    getWalletAddress(curr)
    setIsOpen(false);
  };
  const toggleDropdown = () => setIsOpen(!isOpen);


  const tabs = [
    {
      key: "Fiat",
      label: t("wallet.fiat"),
      content: (
        <>
          {/* <DepositForm activeTab="Deposit" action="fiat" ></DepositForm> */}
          {!isBackOfficeUser() && (!userDetails?.MobileNumber && !siteConfig?.isContactOptional) && (
            <div className={`text-lg mb-1 mb-4 ${pageTextColorClass}`}>
              {t("wallet.messages.pleaseShareContactBeforeDeposit")}
            </div>
          )}
          {!showPayment && (isBackOfficeUser() || userDetails?.MobileNumber || siteConfig?.isContactOptional) && (
            <form className="flex flex-col w-full" onSubmit={handleSubmit}>
              <label htmlFor="amount" className={`block mt-3 text-sm ${pageTextColorClass}`} style={{ opacity: 0.7 }}>
                {t("common.amount")}
              </label>
              <input
                type="number"
                id="amount"
                className={`px-4 py-3 mt-2.5 w-full text-sm font-medium placeholder:text-text-color whitespace-nowrap rounded-lg border border-solid bg-inputbox-color border-border-color focus:outline-none leading-[1.4rem] ${inputBoxTextColorClass}`}
                placeholder={t("wallet.enterYourAmount")}
                value={amount || ""}
                min={userDetails?.IsPremium ? undefined : selectedPG?.minDeposit}
                max={userDetails?.IsPremium ? undefined : selectedPG?.maxDeposit}
                onKeyDown={(e) => {
                  if (["-", "+", "e", "E"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const { value } = e.target;
                  setAmount(value === "" ? undefined : Number(value));
                }}
              />
              {amount != null && !userDetails?.IsPremium && ((amount < (selectedPG?.minDeposit || 0)) || amount > (selectedPG?.maxDeposit || 0)) && (
                <span className="mt-1 text-sm text-orange-500">
                  {t("wallet.messages.amountMustBeBetween").replace("{min}", String(selectedPG?.minDeposit || 0)).replace("{max}", String(selectedPG?.maxDeposit || 0))}
                </span>
              )}
              <button
                type="submit"
                className={`p-2.5 mt-4 w-full text-sm whitespace-nowrap bg-border-color rounded-lg border border-border-color border-solid leading-[1.4rem] ${buttonTextColorClass} ${isDisabled ? "opacity-50" : ""
                  }`}
                disabled={isDisabled || (!userDetails?.IsPremium && (amount < (selectedPG?.minDeposit || 0) || amount > (selectedPG?.maxDeposit || 0)))}
              >
                {(isBackOfficeUser() || userDetails?.MobileNumber || siteConfig?.isContactOptional) ? (loading ? t("common.processing") : t("common.submit")) : t("wallet.addMobileNumber")}
              </button>
            </form>
          )
          }

          {showPayment && paymentProps && (
            <PaymentDialog
              paymentProps={paymentProps}
              closeDialog={closeDialog}
              selectedPG={selectedPG}
            />
          )}
        </>
      )
    },
    {
      key: "Crypto",
      label: t("wallet.crypto"),
      content: (
        <>
          {
            cryptoPGList?.length === 0 && activeCurType === "Crypto" ? (
              <div className={`text-lg mb-1 mb-4 ${pageTextColorClass}`}>
                {t("wallet.messages.cryptoGatewayNotAvailable")}
              </div>
            ) : (
              <>
                {
                  currList?.length === 0 ? (
                    <div className={`text-lg m-2 text-center ${pageTextColorClass}`}>
                      {t("wallet.messages.noCryptoCurrencyFound")}
                    </div>
                  ) : (
                    <>
                      <div className="crypto mt-2">
                        <div className="flex justify-between">
                          <div className={`text-lg ${inputBoxTextColorClass}`}>{t("wallet.selectCurrency")}</div>
                          <div className="relative w-1/2 h-[30px]">
                            <button
                              onClick={toggleDropdown}
                              className={`flex items-center h-full justify-between px-3 py-2 w-full text-xs font-medium whitespace-nowrap rounded-lg border border-solid bg-inputbox-color border-slate-800 ${inputBoxTextColorClass}`}
                            >
                              <span className="truncate">
                                {selectedCurrency?.cryptoCurrency
                                  ? `${selectedCurrency?.cryptoCurrency} (${selectedCurrency?.blockChain})`
                                  : ""}
                              </span>
                              <img
                                loading="lazy"
                                src={DownArrow}
                                alt=""
                                className="shrink-0 ml-2 w-2.5 aspect-[1.67]"
                              />
                            </button>

                            {isOpen && (
                              <ul className="absolute z-10 w-full left-0 top-full mt-1 bg-inputbox-color border border-slate-800 rounded-lg shadow-lg">
                                {currList?.map((curr: any) => (
                                  <li
                                    key={curr}
                                    onClick={() => handleCurrencySelect(curr)}
                                    className={`px-3 py-2 text-xs hover:bg-zinc-700 cursor-pointer ${inputBoxTextColorClass}`}
                                  >
                                    {`${curr?.cryptoCurrency} (${curr?.blockChain})`}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>

                        </div>
                        {
                          walletAddress ? (
                            <>
                              {<label className={`block mt-3 text-sm ${inputBoxTextColorClass}`} style={{ opacity: 0.7 }}>{t("wallet.messages.minimumDepositShouldBe").replace("{amount}", String((selectedPG?.minDeposit || 0) / selectedCurrency?.conversions?.deposit_exchange_rate))}</label>}
                              <label htmlFor="wallet" className={`block mt-1 text-sm ${inputBoxTextColorClass}`} style={{ opacity: 0.7 }}>
                                {t("wallet.yourDepositAddress").replace("{currency}", selectedCurrency?.cryptoCurrency || "")}
                              </label>
                              <div
                                id="wallet"
                                className={`flex items-center justify-between px-4 py-3 mt-2.5 w-full text-sm font-medium placeholder:text-text-color whitespace-nowrap rounded-lg border border-solid bg-inputbox-color border-border-color focus:outline-none leading-[1.4rem] ${inputBoxTextColorClass}`}
                              >
                                <span className={`truncate text-xs ${inputBoxTextColorClass}`}>{walletAddress}</span>
                                <ContentCopyIcon
                                  className={`cursor-pointer hover:opacity-70 ml-2 ${inputBoxTextColorClass}`}
                                  onClick={() => copyToClipboard(walletAddress)}
                                  titleAccess={t("wallet.messages.copyToClipboard")}
                                />
                              </div>
                              <div className="mt-2.5 flex justify-center w-full">
                                <div className="p-1 bg-white">
                                  <QRCode value={walletAddress} size={128} />
                                </div>
                              </div>
                              <div className={`flex flex-col items-center mt-1 ${inputBoxTextColorClass}`}>
                                <p className={`text-center text-sm ${inputBoxTextColorClass}`}>
                                  {t("wallet.messages.scanQRCodeOrCopyAddress")}
                                </p>
                              </div>
                            </>
                          ) : <>
                          </>
                        }
                      </div>
                    </>
                  )
                }
              </>
            )
          }
        </>
      )
    },

  ]

  // Only show tabs that have corresponding list items
  const availableTabs = tabs.filter((tab) =>
    (tab.key === 'Fiat' && fiatPGList.length > 0) ||
    (tab.key === 'Crypto' && cryptoPGList.length > 0)
  );


  return (
    <>
      {isDemo ? (
        <div className="p-4 rounded-lg text-center">
          <p className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-400 font-medium">
            {t("wallet.messages.demoAccountMessage")}
          </p>
        </div>
      ) : (
        <div className="deposit-tabs">
          <div className="wallet-nav">
          <div className="nav nav-pills" role="tablist">
            {availableTabs.map((tab) => (
              <div className="nav-item" key={tab.key}>
                <a
                  role="tab"
                  className={`nav-link ${activeCurType === tab.key ? `active ${activeTabTextColorClass}` : tabTextColorClass}`}
                  onClick={() => handleTabClick(tab.key)}
                >
                  {tab.label}
                </a>
              </div>
            ))}
          </div>

          {/* PG List display below tab label */}
          {!showPayment && activeCurType === "Fiat" && fiatPGList.length > 0 && (
            <div className="nav nav-pills pt-2" role="tablist">
              {fiatPGList?.map((pg: any) => (
                <div className="" key={pg.accountId}>
                  <a
                    role="tab"
                    className={`nav-link pg-tab ${selectedPG?.tgProviderId === pg?.tgProviderId ? `active ${activeTabTextColorClass}` : tabTextColorClass}`}
                    onClick={() => setSelectedPG(pg)}
                  >
                    {pg?.applicationName}
                  </a>
                </div>
              ))}
            </div>
          )}

          {activeCurType === "Crypto" && cryptoPGList.length > 0 && (
            <div className="nav nav-pills pt-2" role="tablist">
              {cryptoPGList?.map((pg: any) => (
                <div className="" key={pg.accountId}>
                  <a
                    role="tab"
                    className={`nav-link pg-tab ${selectedPG?.tgProviderId === pg?.tgProviderId ? `active ${activeTabTextColorClass}` : tabTextColorClass}`}
                    onClick={() => setSelectedPG(pg)}
                  >
                    {pg?.applicationName}
                  </a>
                </div>
              ))}
            </div>
          )}

          <div className="tab-content">
            {availableTabs.map((tab) => (
              <div
                key={tab.key}
                className={`tab-pane fade ${activeCurType === tab.key ? "show active" : ""
                  }`}
                id={`tabs-tabpane-${tab.key}`}
                role="tabpanel"
              >
                {activeCurType === tab.key && tab.content}
              </div>
            ))}
          </div>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    userDetails: state.userDetails?.userDetails,
    siteConfig: state.userDetails?.siteConfig,
    dynamicTextColors: state.userDetails?.dynamicTextColors,
  };
};


export default connect(mapStateToProps)(DepositForm);
